import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import TopBar from '../components/TopBar.jsx';
import { apiGet, apiPost, apiDel } from '../lib/api.js';
import { autoDetectColumnMapping } from '../lib/autoDetectColumnMapping.js';
import toast from 'react-hot-toast';
import {
  Upload, ArrowLeft, Check, SkipForward, Plus, Save, ChevronDown, X,
} from 'lucide-react';

// ─── Canonical fields (mirrors backend CANONICAL_FIELDS) ───────────────────
const CANONICAL_FIELDS = [
  { key: 'firstName', label: 'First Name', required: true },
  { key: 'lastName', label: 'Last Name' },
  // Full Name is a convenience column — the server splits it into First/Last
  // when the CSV only has a combined name field. Explicit First/Last always win.
  { key: 'fullName', label: 'Full Name' },
  { key: 'ageRange', label: 'Age Range' },
  { key: 'existingLifeInsurance', label: 'Current Coverage' },
  { key: 'smoker', label: 'Smoker' },
  { key: 'email', label: 'Email' },
  { key: 'phone', label: 'Phone', required: true },
  { key: 'state', label: 'State' },
  { key: 'city', label: 'City' },
  { key: 'zip', label: 'ZIP' },
  { key: 'timezone', label: 'Timezone' },
  { key: 'vertical', label: 'Vertical' },
  { key: 'tier', label: 'Tier' },
  { key: 'leadVendorName', label: 'Lead Vendor' },
  { key: 'leadType', label: 'Lead Type' },
  { key: 'age', label: 'Age' },
  { key: 'birthday', label: 'DOB / Birthday' },
  { key: 'beneficiary', label: 'Beneficiary' },
  { key: 'protectionGoal', label: 'Protection Goal' },
  { key: 'mortgageAmount', label: 'Mortgage Amount' },
  { key: 'quote', label: 'Quote' },
  { key: 'notes', label: 'Notes' },
  { key: 'tags', label: 'Tags' },
];

// ─── CSV parser (identical to backend parseCSV) ─────────────────────────────
function parseCSV(text) {
  // Strip UTF-8 BOM (Excel adds this when saving as CSV)
  if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
  const rows = [];
  let row = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (inQuotes) {
      if (c === '"' && text[i + 1] === '"') { field += '"'; i++; }
      else if (c === '"') { inQuotes = false; }
      else { field += c; }
    } else {
      if (c === '"') inQuotes = true;
      else if (c === ',') { row.push(field); field = ''; }
      else if (c === '\n') { row.push(field); rows.push(row); row = []; field = ''; }
      else if (c === '\r') { /* skip */ }
      else field += c;
    }
  }
  if (field || row.length) { row.push(field); rows.push(row); }
  return rows.filter((r) => r.length && r.some((v) => v !== ''));
}

// ─── Preview values helper ──────────────────────────────────────────────────
function getPreviewValues(rows, headerIdx, skipHeader = true, count = 3) {
  const startRow = skipHeader ? 1 : 0;
  const vals = [];
  for (let i = startRow; i < rows.length && vals.length < count; i++) {
    const v = rows[i]?.[headerIdx];
    if (v != null && v !== '') vals.push(v);
  }
  return vals;
}

// ═══════════════════════════════════════════════════════════════════════════
export default function LeadImport() {
  const navigate = useNavigate();
  const fileInputRef = useRef(null);

  // ── State (spec §State) ──────────────────────────────────────────────────
  const [step, setStep] = useState(1);
  const [fileName, setFileName] = useState('');
  const [csvText, setCsvText] = useState('');
  const [rows, setRows] = useState([]);        // all parsed rows including header
  const [headers, setHeaders] = useState([]);  // first row
  const [skipHeaderRow, setSkipHeaderRow] = useState(true);
  const [mapping, setMapping] = useState({}); // { csvHeader: canonicalKey | '__custom__:fieldName' }
  // { detected, total } summary for the auto-detect banner on the mapping step.
  const [autoDetected, setAutoDetected] = useState({ detected: 0, total: 0 });
  const [skipped, setSkipped] = useState(new Set()); // csvHeader names explicitly skipped
  const [assignee, setAssignee] = useState('me'); // 'me' | 'round-robin' | <agentId>
  const [presets, setPresets] = useState([]); // loaded via GET /api/mapping-presets
  const [presetName, setPresetName] = useState('');
  const [verified, setVerified] = useState(false);
  const [attestConsent, setAttestConsent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  // ── Duplicate settings (Ringy "Step 6" parity — both OPT-IN, default OFF) ──
  const [dupeAccountEnabled, setDupeAccountEnabled] = useState(false);
  const [dupeAccountWindowDays, setDupeAccountWindowDays] = useState(30);
  const [dupeTextedEnabled, setDupeTextedEnabled] = useState(false);
  const [dupeTextedWindowDays, setDupeTextedWindowDays] = useState(30);

  // ── Lead Vendor (optional — fires AI Text Bot auto-outbound) ─────────────
  const [vendors, setVendors] = useState([]); // loaded via GET /api/lead-vendors
  const [selectedVendorId, setSelectedVendorId] = useState('');
  const [vendorBotName, setVendorBotName] = useState(''); // resolved from textBotId for the info card

  // ── Extra UI state ───────────────────────────────────────────────────────
  const [agents, setAgents] = useState([]);
  const [showPresets, setShowPresets] = useState(false);
  // Per-header: track whether "custom field" mode is active and the typed name
  const [customInputs, setCustomInputs] = useState({}); // { csvHeader: typedCustomName }
  // Extra columns added via "+ ADD COLUMN"
  const [extraHeaders, setExtraHeaders] = useState([]);

  // ── Load agents + presets + vendors on mount ─────────────────────────────
  useEffect(() => {
    apiGet('/agents').then((r) => setAgents(r.agents || [])).catch(() => {});
    apiGet('/mapping-presets').then((r) => setPresets(r.presets || [])).catch(() => {});
    apiGet('/lead-vendors').then((r) => setVendors(r.vendors || [])).catch(() => {});
  }, []);

  // ── Selected vendor's autoOutbound config (derived) ───────────────────────
  const selectedVendor = vendors.find((v) => v._id === selectedVendorId) || null;
  const vendorAutoOutbound = selectedVendor?.autoOutbound || null;
  const vendorFiresAiTextBot = !!(vendorAutoOutbound?.mode === 'ai_text_bot' && vendorAutoOutbound?.textBotId);

  // Resolve the bot's display name for the info card whenever the selected
  // vendor's textBotId changes. Falls back to "the configured bot" if the
  // lookup fails (bot deleted, permission issue, network hiccup, etc.) — the
  // card should never block or error out over a missing display name.
  useEffect(() => {
    if (!vendorFiresAiTextBot) { setVendorBotName(''); return; }
    const botId = vendorAutoOutbound.textBotId;
    apiGet(`/text-bots/${botId}`)
      .then((r) => setVendorBotName(r.bot?.name || ''))
      .catch(() => setVendorBotName(''));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vendorFiresAiTextBot, vendorAutoOutbound?.textBotId]);

  // ── File handling ────────────────────────────────────────────────────────
  const handleFile = useCallback((file) => {
    if (!file) return;
    setFileName(file.name);
    const reader = new FileReader();
    reader.onload = (e) => {
      let text = e.target.result;
      // Strip BOM from text we'll send to the server too — keeps mapping keys clean.
      if (text.charCodeAt(0) === 0xFEFF) text = text.slice(1);
      const parsed = parseCSV(text);
      if (!parsed.length) { toast.error('Empty or invalid CSV'); return; }
      // Trim/clean header strings so they match values keyed in mapping
      const cleanHeaders = (parsed[0] || []).map((h) => String(h ?? '').replace(/^\uFEFF/, '').trim());
      parsed[0] = cleanHeaders;
      setCsvText(text);
      setRows(parsed);
      const hdrs = cleanHeaders;
      setHeaders(hdrs);
      // Pre-select targets whose header text matches a field name. Only fills
      // slots that aren't already mapped — never overwrites a user choice.
      const detected = autoDetectColumnMapping(hdrs, CANONICAL_FIELDS);
      setMapping(detected);
      setAutoDetected({ detected: Object.keys(detected).length, total: hdrs.length });
      setSkipped(new Set());
      setCustomInputs({});
      setExtraHeaders([]);
      setVerified(false);
      setStep(2);
    };
    reader.readAsText(file);
  }, []);

  function onFileChange(e) {
    handleFile(e.target.files?.[0]);
    // Reset input value so the same file can be re-selected
    e.target.value = '';
  }

  // ── Reset / back ─────────────────────────────────────────────────────────
  function reset() {
    setStep(1);
    setFileName('');
    setCsvText('');
    setRows([]);
    setHeaders([]);
    setMapping({});
    setAutoDetected({ detected: 0, total: 0 });
    setSkipped(new Set());
    setCustomInputs({});
    setExtraHeaders([]);
    setVerified(false);
    setResult(null);
    setSelectedVendorId('');
  }

  // ── Preset actions ───────────────────────────────────────────────────────
  function applyPreset(preset) {
    setMapping(preset.mapping || {});
    setSkipped(new Set());
    setShowPresets(false);
    toast.success(`Loaded preset "${preset.name}"`);
  }

  async function savePreset() {
    if (!presetName.trim()) { toast.error('Enter a preset name'); return; }
    // Collect custom field names from mapping values
    const customFieldNames = Object.values(mapping)
      .filter((v) => v && v.startsWith('__custom__:'))
      .map((v) => v.slice('__custom__:'.length));
    try {
      await apiPost('/mapping-presets', {
        name: presetName.trim(),
        mapping,
        customFields: [...new Set(customFieldNames)],
      });
      toast.success(`Preset "${presetName.trim()}" saved`);
      setPresetName('');
      // Refresh presets list
      apiGet('/mapping-presets').then((r) => setPresets(r.presets || [])).catch(() => {});
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to save preset');
    }
  }

  async function deletePreset(id) {
    try {
      await apiDel(`/mapping-presets/${id}`);
      setPresets((p) => p.filter((x) => x._id !== id));
      toast.success('Preset deleted');
    } catch (e) {
      toast.error('Failed to delete preset');
    }
  }

  // ── Mapping helpers ──────────────────────────────────────────────────────
  function setFieldMapping(header, value) {
    setMapping((prev) => ({ ...prev, [header]: value }));
  }

  function toggleSkip(header) {
    setSkipped((prev) => {
      const next = new Set(prev);
      if (next.has(header)) next.delete(header);
      else next.add(header);
      return next;
    });
  }

  function addExtraColumn() {
    const placeholder = `custom_col_${extraHeaders.length + 1}`;
    setExtraHeaders((prev) => [...prev, placeholder]);
  }

  // ── Derived: all display headers (CSV headers + extra) ───────────────────
  const allHeaders = [...headers, ...extraHeaders];

  // ── Phone mapped check ───────────────────────────────────────────────────
  const phoneMapped = Object.entries(mapping).some(
    ([h, v]) => !skipped.has(h) && v === 'phone'
  );

  // ── Submit ───────────────────────────────────────────────────────────────
  async function handleImport() {
    if (!phoneMapped) { toast.error('You must map a Phone column before importing'); return; }
    if (!verified) { toast.error('Please verify your column mapping first'); return; }

    setBusy(true);
    try {
      // Separate canonical from custom mapping, excluding skipped columns
      const canonicalMapping = {};
      const customMapping = {};
      Object.entries(mapping).forEach(([h, v]) => {
        if (skipped.has(h)) return;
        if (!v) return;
        if (v.startsWith('__custom__:')) customMapping[h] = v.slice('__custom__:'.length);
        else canonicalMapping[h] = v;
      });

      // Build combined mapping for backend (re-encodes custom keys)
      const combinedMapping = {
        ...canonicalMapping,
        ...Object.fromEntries(
          Object.entries(customMapping).map(([h, k]) => [h, `__custom__:${k}`])
        ),
      };

      const res = await apiPost('/imports/leads/commit', {
        text: csvText,
        mapping: combinedMapping,
        defaults: {},
        dedupe: 'phone',
        assignee,
        skipHeaderRow,
        vendorId: selectedVendorId || undefined,
        dupeAccountEnabled,
        dupeAccountWindowDays,
        dupeTextedEnabled,
        dupeTextedWindowDays,
        attestConsent,
      });

      setResult(res);
      setStep(3);

      // Surface the vendor auto-outbound outcome alongside the normal
      // import-complete flow (the Step 3 screen already shows full detail).
      const scheduledCount = res?.csvImport?.autoOutboundScheduled ?? 0;
      if (scheduledCount > 0) {
        toast.success(`AI Text Bot scheduled for ${scheduledCount} lead${scheduledCount === 1 ? '' : 's'}.`);
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.message || 'Import failed');
    } finally {
      setBusy(false);
    }
  }

  // ── Computed row count (excluding header if skipHeaderRow) ───────────────
  const dataRowCount = rows.length > 0 ? (skipHeaderRow ? rows.length - 1 : rows.length) : 0;

  // ═════════════════════════════════════════════════════════════════════════
  // RENDER
  // ═════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-4">
      <TopBar
        title="Upload CSV"
        subtitle={step === 1 ? 'Import leads from a CSV file' : step === 2 ? 'Map your CSV columns' : 'Import complete'}
      />

      {/* ── STEP 1: File picker ───────────────────────────────────────────── */}
      {step === 1 && (
        <div className="panel p-6 space-y-6 max-w-2xl mx-auto">
          {/* Back button */}
          <button
            onClick={() => navigate('/leads')}
            className="flex items-center gap-2 text-sm text-white/60 hover:text-white transition"
          >
            <ArrowLeft className="w-4 h-4" /> Back to Leads
          </button>

          <h2 className="text-xl font-semibold">Upload CSV</h2>

          {/* Assignee selector */}
          <div>
            <label className="label text-sm text-white/70 mb-1 block">
              I am uploading leads for:
            </label>
            <select
              className="input"
              value={assignee}
              onChange={(e) => setAssignee(e.target.value)}
            >
              <option value="me">Myself</option>
              <option value="round-robin">Round-robin distribution</option>
              {agents.map((a) => (
                <option key={a._id} value={a._id}>
                  {a.name}
                </option>
              ))}
            </select>
          </div>

          {/* Lead Vendor selector — optional, fires AI Text Bot auto-outbound */}
          <div>
            <label className="label text-sm text-white/70 mb-1 block">
              Lead Vendor{' '}
              <span className="text-white/40 font-normal">
                (optional — fires AI Text Bot if configured)
              </span>
            </label>
            <select
              className="input"
              value={selectedVendorId}
              onChange={(e) => setSelectedVendorId(e.target.value)}
            >
              <option value="">None (skip AI outbound)</option>
              {vendors.map((v) => (
                <option key={v._id} value={v._id}>
                  {v.name}
                </option>
              ))}
            </select>

            {/* Info card: reflects the selected vendor's autoOutbound config */}
            {selectedVendor && vendorFiresAiTextBot && (
              <div className="mt-2 flex items-start gap-2 rounded-lg border border-emerald-400/30 bg-emerald-400/10 px-3 py-2 text-xs text-emerald-300">
                <Check className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                <span>
                  Will send AI opener via {vendorBotName || 'the configured bot'} after{' '}
                  {vendorAutoOutbound.delayMinutes ?? 3} min
                </span>
              </div>
            )}
            {selectedVendor && !vendorFiresAiTextBot && (
              <div className="mt-2 text-xs text-white/40">
                AI outbound not configured on this vendor.
              </div>
            )}
          </div>

          {/* Skip first row */}
          <label className="flex items-center gap-3 cursor-pointer select-none text-sm">
            <input
              type="checkbox"
              checked={skipHeaderRow}
              onChange={(e) => setSkipHeaderRow(e.target.checked)}
              className="w-4 h-4 accent-civix-purple"
            />
            Skip first row in file{' '}
            <span className="text-white/50">(check if first row is header)</span>
          </label>

          {/* File drop zone */}
          <div
            className="border-2 border-dashed border-civix-border rounded-xl p-10 text-center hover:border-civix-purple transition cursor-pointer"
            onClick={() => fileInputRef.current?.click()}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleFile(e.dataTransfer.files?.[0]);
            }}
          >
            <Upload className="w-10 h-10 mx-auto text-civix-purple mb-3" />
            <div className="font-semibold mb-1">Drop your CSV here</div>
            <div className="text-sm text-white/50 mb-4">or click to browse — max 25 MB</div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              hidden
              onChange={onFileChange}
            />
            <button
              type="button"
              className="btn-primary"
              onClick={(e) => { e.stopPropagation(); fileInputRef.current?.click(); }}
            >
              Choose CSV File
            </button>
          </div>

          {/* Load preset dropdown */}
          <div className="relative">
            <button
              type="button"
              className="btn-ghost flex items-center gap-2 text-sm"
              onClick={() => setShowPresets((v) => !v)}
            >
              <ChevronDown className="w-4 h-4" />
              Load CSV Column Mapping
            </button>
            {showPresets && (
              <div className="absolute left-0 mt-1 w-72 panel border border-civix-border rounded-lg shadow-xl z-50 overflow-hidden">
                {presets.length === 0 ? (
                  <div className="p-4 text-sm text-white/50">No saved presets yet</div>
                ) : (
                  <ul>
                    {presets.map((p) => (
                      <li
                        key={p._id}
                        className="flex items-center justify-between px-4 py-2.5 hover:bg-civix-panelLight cursor-pointer border-b border-civix-border/40 last:border-0"
                      >
                        <span
                          className="text-sm flex-1"
                          onClick={() => applyPreset(p)}
                        >
                          {p.name}
                        </span>
                        <button
                          onClick={(e) => { e.stopPropagation(); deletePreset(p._id); }}
                          className="text-white/30 hover:text-rose-400 transition ml-2"
                          title="Delete preset"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── STEP 2: Column mapping ───────────────────────────────────────── */}
      {step === 2 && (
        <div className="panel p-6 space-y-6">
          {/* Header row */}
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="font-semibold text-base leading-tight">
                Describe your columns. To create a custom column name, select the dropdown arrow
                and then begin typing.
              </h2>
              <p className="text-sm text-white/50 mt-1">
                {fileName} — <span className="text-emerald-400 font-semibold">{dataRowCount} rows found</span>
              </p>
            </div>
            <button onClick={reset} className="btn-ghost text-sm shrink-0">
              <ArrowLeft className="w-4 h-4 inline mr-1" /> Back
            </button>
          </div>

          {/* Auto-detect banner */}
          {autoDetected.detected > 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-civix-purple/40 bg-civix-purple/10 px-4 py-2.5 text-sm">
              <Check className="w-4 h-4 text-civix-purple shrink-0" />
              <span>
                Auto-detected{' '}
                <span className="font-semibold">{autoDetected.detected}</span> of{' '}
                <span className="font-semibold">{autoDetected.total}</span> columns. Review and adjust below.
              </span>
            </div>
          )}

          {/* Mapping rows */}
          <div className="space-y-2">
            {allHeaders.map((header, idx) => {
              const isSkipped = skipped.has(header);
              const currentVal = mapping[header] || '';
              const isCustomMode = currentVal.startsWith('__custom__:');
              const customName = isCustomMode ? currentVal.slice('__custom__:'.length) : (customInputs[header] || '');
              const previewVals = idx < headers.length
                ? getPreviewValues(rows, idx, skipHeaderRow)
                : [];
              const isExtraCol = idx >= headers.length;

              return (
                <div
                  key={header + idx}
                  className={`flex flex-wrap md:flex-nowrap items-start gap-4 p-4 rounded-lg border transition ${
                    isSkipped
                      ? 'border-rose-400/30 bg-rose-400/5 opacity-60'
                      : 'border-civix-border bg-civix-panelLight'
                  }`}
                >
                  {/* Left: column label */}
                  <div className="w-48 shrink-0">
                    <div className="text-xs text-white/40 uppercase tracking-wider mb-0.5">
                      CSV header name:
                    </div>
                    <div className="font-semibold text-sm truncate">{header}</div>
                    {!isExtraCol && (
                      <div className="text-xs text-white/40 mt-0.5 font-mono">{header}</div>
                    )}
                  </div>

                  {/* Center: field dropdown */}
                  <div className="flex-1 min-w-[180px]">
                    {isCustomMode ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          className="input flex-1 text-sm"
                          placeholder="Custom field name…"
                          value={customName}
                          autoFocus
                          onChange={(e) => {
                            const val = e.target.value;
                            setFieldMapping(header, val ? `__custom__:${val}` : '');
                          }}
                        />
                        <button
                          type="button"
                          className="text-white/40 hover:text-white transition"
                          title="Clear"
                          onClick={() => setFieldMapping(header, '')}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <select
                        className="input text-sm"
                        value={currentVal}
                        disabled={isSkipped}
                        onChange={(e) => {
                          const val = e.target.value;
                          if (val === '__custom__') {
                            // Switch to custom input mode
                            setFieldMapping(header, '__custom__:');
                          } else {
                            setFieldMapping(header, val);
                          }
                        }}
                      >
                        <option value="">— Unmapped / Skip —</option>
                        {CANONICAL_FIELDS.map((f) => (
                          <option key={f.key} value={f.key}>
                            {f.label}{f.required ? ' *' : ''}
                          </option>
                        ))}
                        <option value="__custom__">+ Custom field…</option>
                      </select>
                    )}
                  </div>

                  {/* Right: preview values */}
                  <div className="w-48 shrink-0">
                    {previewVals.length > 0 && (
                      <>
                        <div className="text-xs text-white/40 uppercase tracking-wider mb-0.5">
                          Preview information:
                        </div>
                        {previewVals.map((v, i) => (
                          <div
                            key={i}
                            className="text-xs text-white/60 truncate max-w-48"
                          >
                            {v}
                          </div>
                        ))}
                      </>
                    )}
                  </div>

                  {/* Far right: SKIP COLUMN */}
                  <div className="shrink-0 self-center">
                    <button
                      type="button"
                      onClick={() => toggleSkip(header)}
                      className={`text-xs px-3 py-1.5 rounded border uppercase tracking-wide font-semibold transition ${
                        isSkipped
                          ? 'border-rose-400 text-rose-400 bg-rose-400/10'
                          : 'border-civix-border text-white/50 hover:border-rose-400/60 hover:text-rose-400/80'
                      }`}
                    >
                      {isSkipped ? 'Skipped' : 'Skip Column'}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          {/* + ADD COLUMN */}
          <button
            type="button"
            onClick={addExtraColumn}
            className="flex items-center gap-2 text-sm text-civix-purple hover:text-white transition font-semibold"
          >
            <Plus className="w-4 h-4" /> ADD COLUMN
          </button>

          {/* Divider */}
          <hr className="border-civix-border" />

          {/* Save preset */}
          <div className="flex items-center gap-3">
            <span className="text-sm font-medium text-white/70 shrink-0">
              Save CSV Column Mapping?
            </span>
            <input
              type="text"
              className="input flex-1 max-w-xs text-sm"
              placeholder="CSV Column Mapping Name"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') savePreset(); }}
            />
            <button
              type="button"
              onClick={savePreset}
              className="btn-primary flex items-center gap-2 text-sm"
            >
              <Save className="w-4 h-4" /> SAVE
            </button>
          </div>

          {/* Divider */}
          <hr className="border-civix-border" />

          {/* Duplicate settings (Ringy "Step 6" parity) */}
          <div className="space-y-3">
            <span className="text-sm font-medium text-white/70">Duplicate settings</span>

            <div className="border border-civix-border rounded-lg p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer select-none text-sm">
                <input
                  type="checkbox"
                  checked={dupeAccountEnabled}
                  onChange={(e) => setDupeAccountEnabled(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-civix-purple"
                />
                <span>
                  Skip any leads that share the same phone number or email as a lead already in my account.
                </span>
              </label>
              {dupeAccountEnabled && (
                <div className="flex items-center gap-2 text-sm text-white/70 pl-7">
                  <span>Skip leads that have been in my account for the past</span>
                  <select
                    className="input text-sm py-1"
                    value={dupeAccountWindowDays}
                    onChange={(e) => setDupeAccountWindowDays(Number(e.target.value))}
                  >
                    {[30, 60, 90, 120].map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <span>Days</span>
                </div>
              )}
            </div>

            <div className="border border-civix-border rounded-lg p-4 space-y-3">
              <label className="flex items-start gap-3 cursor-pointer select-none text-sm">
                <input
                  type="checkbox"
                  checked={dupeTextedEnabled}
                  onChange={(e) => setDupeTextedEnabled(e.target.checked)}
                  className="w-4 h-4 mt-0.5 accent-civix-purple"
                />
                <span>Skip any leads I have texted recently.</span>
              </label>
              {dupeTextedEnabled && (
                <div className="flex items-center gap-2 text-sm text-white/70 pl-7">
                  <span>Skip leads texted within the past</span>
                  <select
                    className="input text-sm py-1"
                    value={dupeTextedWindowDays}
                    onChange={(e) => setDupeTextedWindowDays(Number(e.target.value))}
                  >
                    {[30, 60, 90, 120].map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                  <span>Days</span>
                </div>
              )}
            </div>
          </div>

          {/* Divider */}
          <hr className="border-civix-border" />

          {/* Verified checkbox */}
          <div className="border border-civix-purple/40 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer select-none text-sm">
              <input
                type="checkbox"
                checked={verified}
                onChange={(e) => setVerified(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-civix-purple"
              />
              <span>
                I have verified the columns I set above are accurate.
              </span>
            </label>
          </div>

          {/* TCPA attestation checkbox — enables AI texting for imported leads */}
          <div className="border border-amber-300 bg-amber-50 rounded-lg p-4">
            <label className="flex items-start gap-3 cursor-pointer select-none text-sm">
              <input
                type="checkbox"
                checked={attestConsent}
                onChange={(e) => setAttestConsent(e.target.checked)}
                className="w-4 h-4 mt-0.5 accent-amber-600"
              />
              <span className="text-amber-900">
                I attest these leads have valid TCPA/express-written consent. CIVIX stamps the
                audit trail; the agent bears compliance responsibility.{' '}
                <span className="font-medium">Enables AI texting for these leads.</span>
              </span>
            </label>
          </div>

          {/* Action buttons */}
          <div className="flex items-center justify-between gap-4">
            <button
              type="button"
              onClick={reset}
              className="btn-ghost"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleImport}
              disabled={busy || !verified || !phoneMapped}
              className="btn-primary disabled:opacity-40 flex items-center gap-2"
              title={
                !phoneMapped
                  ? 'Map a Phone column to enable import'
                  : !verified
                  ? 'Check the verification box to enable import'
                  : ''
              }
            >
              {busy ? 'Importing…' : `Import ${dataRowCount} leads`}
            </button>
          </div>

          {!phoneMapped && (
            <p className="text-xs text-rose-400 text-right -mt-2">
              Phone column must be mapped before importing.
            </p>
          )}
        </div>
      )}

      {/* ── STEP 3: Result ───────────────────────────────────────────────── */}
      {step === 3 && result && (
        <div className="panel p-10 text-center max-w-xl mx-auto">
          <div className="w-16 h-16 mx-auto rounded-full bg-civix-purple/20 flex items-center justify-center mb-5">
            <Check className="w-8 h-8 text-civix-purple" />
          </div>

          <h2 className="text-2xl font-bold mb-2">
            Imported {result.inserted ?? 0} leads
          </h2>
          <p className="text-white/50 text-sm mb-2">
            {fileName && `From ${fileName}`}
          </p>
          <p className="text-white/40 text-xs mb-6">
            {result.total ?? 0} rows processed · {result.inserted ?? 0} inserted · {result.duplicates ?? 0} duplicates · {result.invalid ?? 0} invalid
            {(result.filterSkipped?.account ?? 0) > 0 && ` · ${result.filterSkipped.account} account duplicates`}
            {(result.filterSkipped?.texted ?? 0) > 0 && ` · ${result.filterSkipped.texted} texted recently`}
          </p>

          {/* Breakdown */}
          <div className="grid grid-cols-3 gap-3 mb-8">
            <div className="panel p-4">
              <div className="text-2xl font-semibold gradient-text">{result.inserted ?? 0}</div>
              <div className="text-xs text-white/50 mt-1">Imported</div>
            </div>
            <div className="panel p-4">
              <div className="text-2xl font-semibold text-amber-400">{result.duplicates ?? 0}</div>
              <div className="text-xs text-white/50 mt-1">Duplicates skipped</div>
            </div>
            <div className="panel p-4">
              <div className="text-2xl font-semibold text-rose-400">{result.invalid ?? 0}</div>
              <div className="text-xs text-white/50 mt-1">Invalid skipped</div>
            </div>
          </div>

          {/* Vendor AI Text Bot auto-outbound outcome */}
          {(result.csvImport?.autoOutboundScheduled ?? 0) > 0 && (
            <div className="text-left mb-6 panel p-4 border border-emerald-400/30">
              <div className="text-xs text-emerald-300 uppercase tracking-wider mb-1 font-semibold">
                AI Text Bot scheduled
              </div>
              <p className="text-xs text-white/70">
                AI Text Bot scheduled for {result.csvImport.autoOutboundScheduled} lead
                {result.csvImport.autoOutboundScheduled === 1 ? '' : 's'}.
              </p>
            </div>
          )}

          {/* Skipped reason list (if present) */}
          {result.skipped && result.skipped.length > 0 && (
            <div className="text-left mb-6 panel p-4 max-h-40 overflow-y-auto">
              <div className="text-xs text-white/40 uppercase tracking-wider mb-2">Skip reasons</div>
              {result.skipped.slice(0, 20).map((s, i) => (
                <div key={i} className="text-xs text-white/60 py-0.5 border-b border-civix-border/30 last:border-0">
                  {s.phone || s.email || 'row'} — {s.reason}
                </div>
              ))}
              {result.skipped.length > 20 && (
                <div className="text-xs text-white/40 mt-1">+ {result.skipped.length - 20} more</div>
              )}
            </div>
          )}

          {/* Zero-row diagnostic */}
          {(result.total ?? 0) === 0 && (
            <div className="text-left mb-6 panel p-4 border border-amber-400/30">
              <div className="text-xs text-amber-400 uppercase tracking-wider mb-2 font-semibold">No rows were processed</div>
              <p className="text-xs text-white/70 mb-2">
                The server received your file but produced 0 rows after applying your column mapping. Most common causes:
              </p>
              <ul className="text-xs text-white/60 list-disc list-inside space-y-1">
                <li>None of your CSV columns are mapped to a Phone field</li>
                <li>The mapping object’s keys don’t match the actual CSV header text (BOM/whitespace mismatch)</li>
                <li>The CSV uses a non-comma delimiter (semicolon, tab)</li>
              </ul>
              <p className="text-xs text-white/50 mt-3">
                Click “Upload another CSV” below, then on the mapping screen verify Phone is set to a real column with phone-like preview values.
              </p>
            </div>
          )}

          <div className="flex justify-center gap-3">
            <button type="button" onClick={reset} className="btn-ghost">
              Upload another CSV
            </button>
            <button
              type="button"
              onClick={() => navigate('/leads')}
              className="btn-primary"
            >
              View Leads
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
